/**
 * Care objective MEASURE helpers — pure formatting + progress maths shared by
 * every surface that renders a measurable objective (business-web panel +
 * dashboards, journey-web read view, citizen-mobile self-measure). Framework-
 * agnostic so all three apps compute identical progress/labels.
 *
 * Operates on the measurable fields the objective carries (migration 0080).
 */

export type CareMeasureType = 'number' | 'scale' | 'percent' | 'milestone' | 'rating';
export type CareMeasureDirection = 'increase' | 'decrease' | 'reach';

/** The measurable subset of a `CareObjective` these helpers read. */
export interface MeasurableObjective {
  measureType: CareMeasureType;
  measureUnit: string | null;
  measureDirection: CareMeasureDirection;
  measureMin: number | null;
  measureMax: number | null;
  scaleLabels: string[] | null;
  baselineValue: number | null;
  targetValue: number | null;
}

/**
 * Naive English singular for a free-text measure unit stored in its PLURAL form
 * (e.g. "sessions"). When the count is exactly ±1 we singularize ("1 session");
 * otherwise the stored plural is kept ("6 sessions"). Best-effort for the
 * English catalog: a "…ss" (progress) or non-`s` unit is left untouched, so it
 * never mangles a word that isn't a plural. Non-English units simply stay as
 * authored (company catalog data).
 */
function unitForCount(unit: string, value: number): string {
  if (Math.abs(value) !== 1) return unit;
  if (/[^aeiou]ies$/i.test(unit)) return unit.replace(/ies$/i, 'y'); // activities → activity
  if (/s$/i.test(unit) && !/ss$/i.test(unit)) return unit.replace(/s$/i, ''); // sessions → session
  return unit;
}

/** Display a measured value per its measure type (e.g. "B2", "8 visits", "80%", "Done"). */
export function formatMeasureValue(
  value: number | null | undefined,
  o: Pick<MeasurableObjective, 'measureType' | 'measureUnit' | 'scaleLabels'>,
  labels: { yes: string; no: string }
): string {
  if (value == null || Number.isNaN(value)) return '—';
  switch (o.measureType) {
    case 'scale':
      return o.scaleLabels?.[Math.round(value)] ?? String(value);
    case 'percent':
      return `${Math.round(value)}%`;
    case 'milestone':
      return value >= 1 ? labels.yes : labels.no;
    case 'rating':
      return `${value}/5`;
    case 'number':
      return o.measureUnit ? `${value} ${unitForCount(o.measureUnit, value)}` : String(value);
    default: {
      // Exhaustiveness guard — a new measure type must be handled above, or this
      // line fails the build instead of silently rendering a raw number.
      const _exhaustive: never = o.measureType;
      return String(_exhaustive);
    }
  }
}

/**
 * Progress (0–100) of `latest` toward `target` from `baseline`, honoring the
 * measure direction. Returns null when there's no target / no measurement yet —
 * or, for a `decrease` objective, no baseline (the "from where" is required to
 * say how far the talent has come down).
 */
export function progressPercent(
  latest: number | null | undefined,
  o: Pick<MeasurableObjective, 'measureType' | 'baselineValue' | 'targetValue'> & {
    measureDirection?: CareMeasureDirection;
  }
): number | null {
  if (o.targetValue == null || latest == null || Number.isNaN(latest)) return null;
  if (o.measureType === 'milestone') return latest >= (o.targetValue || 1) ? 100 : 0;

  const clamp = (p: number) => Math.max(0, Math.min(100, Math.round(p)));

  if (o.measureDirection === 'decrease') {
    // Coming DOWN from a known baseline to a lower target.
    if (o.baselineValue == null) return null;
    const span = o.baselineValue - o.targetValue;
    if (span <= 0) return latest <= o.targetValue ? 100 : 0;
    return clamp(((o.baselineValue - latest) / span) * 100);
  }

  // increase | reach (default): going UP from baseline (0 when unset) to target.
  const base = o.baselineValue ?? 0;
  const span = o.targetValue - base;
  if (span === 0) return latest >= o.targetValue ? 100 : 0;
  return clamp(((latest - base) / span) * 100);
}

/** Is `latest` at/past the target (per direction)? */
export function isOnTarget(
  latest: number | null | undefined,
  o: Pick<MeasurableObjective, 'measureType' | 'measureDirection' | 'targetValue'>
): boolean {
  if (o.targetValue == null || latest == null) return false;
  if (o.measureType === 'milestone') return latest >= (o.targetValue || 1);
  return o.measureDirection === 'decrease' ? latest <= o.targetValue : latest >= o.targetValue;
}

// ── How much of a cohort is on target ──────────────────────────────────

/** Every band an on-target share can have — exported so key maps can be proven complete. */
export const ON_TARGET_SHARE_BANDS = ['most', 'some', 'few'] as const;
export type OnTargetShareBand = (typeof ON_TARGET_SHARE_BANDS)[number];

/** A share this high or better is `most`; this or better `some`; else `few`. */
export const ON_TARGET_SHARE_LOWER_BOUNDS = { most: 67, some: 34 } as const;

/**
 * The share (0–100) of talents with data who are on target for an objective,
 * and its band — computed by the server, drawn by the dashboard (which used to
 * divide and grade it itself). `null` when nobody has data yet.
 */
export function onTargetShare(
  onTargetCount: number,
  withDataCount: number
): { pct: number; band: OnTargetShareBand } | null {
  if (withDataCount <= 0) return null;
  const pct = Math.round((onTargetCount / withDataCount) * 100);
  const band: OnTargetShareBand =
    pct >= ON_TARGET_SHARE_LOWER_BOUNDS.most
      ? 'most'
      : pct >= ON_TARGET_SHARE_LOWER_BOUNDS.some
        ? 'some'
        : 'few';
  return { pct, band };
}
